import { useCallback, useLayoutEffect, useRef } from 'react';
import { SelectItemMode } from '@atlaskit/editor-common/type-ahead';
import { closeTypeAhead } from '../../pm-plugins/commands/close-type-ahead';
import { insertTypeAheadItem } from '../../pm-plugins/commands/insert-type-ahead-item';
import { setSelectionBeforeQuery } from '../../pm-plugins/commands/set-selection-before-query';
import { CloseSelectionOptions } from '../../pm-plugins/constants';
import { itemIsDisabled } from '../../pm-plugins/item-is-disabled';
var onlyHasViewMoreItem = function onlyHasViewMoreItem(items) {
  var _items$at;
  return ((_items$at = items.at(0)) === null || _items$at === void 0 ? void 0 : _items$at.title) === 'View more';
};
var insertRawQuery = function insertRawQuery(_ref) {
  var view = _ref.view,
    setSelectionAt = _ref.setSelectionAt,
    text = _ref.text,
    forceFocusOnEditor = _ref.forceFocusOnEditor;
  var _view$state = view.state,
    tr = _view$state.tr,
    schema = _view$state.schema;
  closeTypeAhead(tr);
  if (text.length > 0) {
    tr.replaceSelectionWith(schema.text(text));
    if (setSelectionAt === CloseSelectionOptions.BEFORE_TEXT_INSERTED) {
      setSelectionBeforeQuery(text)(tr);
    }
  }
  view.dispatch(tr);
  if (forceFocusOnEditor) {
    view.focus();
  }
};
export var useItemInsert = function useItemInsert(triggerHandler, editorView, items, api) {
  var editorViewRef = useRef(editorView);
  var itemsRef = useRef(items);
  var onTextInsert = useCallback(function (_ref2) {
    var setSelectionAt = _ref2.setSelectionAt,
      text = _ref2.text,
      forceFocusOnEditor = _ref2.forceFocusOnEditor;
    if (!triggerHandler) {
      return;
    }
    var view = editorViewRef.current;
    insertRawQuery({
      view: view,
      setSelectionAt: setSelectionAt,
      text: text,
      forceFocusOnEditor: forceFocusOnEditor
    });
  }, [triggerHandler]);
  var onItemInsert = useCallback(function (_ref3) {
    var mode = _ref3.mode,
      index = _ref3.index,
      query = _ref3.query;
    var sourceListItem = itemsRef.current;
    if (sourceListItem.length === 0 || !triggerHandler ||
    // View more item is only added for keyboard navigation and should not be considered as a valid item to be inserted
    onlyHasViewMoreItem(sourceListItem)) {
      var text = "".concat(triggerHandler.trigger).concat(query);
      onTextInsert({
        forceFocusOnEditor: true,
        setSelectionAt: CloseSelectionOptions.AFTER_TEXT_INSERTED,
        text: text
      });
      return;
    }
    var itemToInsert = sourceListItem[index];
    if (!itemToInsert) {
      return;
    }
    var isDisabled = itemIsDisabled(itemToInsert, api);
    if (isDisabled) {
      return;
    }
    var view = editorViewRef.current;
    insertTypeAheadItem(view)({
      item: itemToInsert,
      handler: triggerHandler,
      mode: mode,
      query: query,
      sourceListItem: sourceListItem
    });
  }, [triggerHandler, onTextInsert, api]);
  var onItemMatch = useCallback(function (_ref4) {
    var mode = _ref4.mode,
      query = _ref4.query;
    var _items = itemsRef.current;
    if (_items && _items.length > 1) {
      return false;
    }
    if (_items.length === 1 &&
    // View more item is only added for keyboard navigation and should not be considered as a valid item to be inserted
    !onlyHasViewMoreItem(_items)) {
      queueMicrotask(function () {
        onItemInsert({
          mode: mode,
          query: query,
          index: 0
        });
      });
    } else {
      var text = "".concat(triggerHandler.trigger).concat(query);
      queueMicrotask(function () {
        onTextInsert({
          forceFocusOnEditor: true,
          setSelectionAt: CloseSelectionOptions.AFTER_TEXT_INSERTED,
          text: mode === SelectItemMode.SPACE ? text.concat(' ') : text
        });
      });
    }
    return true;
  }, [onItemInsert, triggerHandler, onTextInsert]);
  useLayoutEffect(function () {
    itemsRef.current = items;
  }, [items]);
  return [onItemInsert, onTextInsert, onItemMatch];
};